<template>
  <b-row>
    <b-col sm="12">
      <b-card header-tag="header" footer-tag="footer">
        <b-row>
          <b-col>
            <h4 id="traffic" class="card-title mb-0">Mensajería Interna</h4>
            <div class="small text-muted">Conversaciones directas con otros integrantes del staff</div>
          </b-col>
        </b-row>
      </b-card>

      <b-card header-tag="header" footer-tag="footer" no-body>
        <b-row no-gutters>
          <b-col md="4" class="messages-inbox-list-col">
            <div class="messages-inbox-list-header">
              <span class="messages-inbox-list-title">Conversaciones</span>
              <b-button size="sm" variant="outline-primary" @click="toggleStaffPicker()">
                <b-icon :icon="showStaffPicker ? 'x' : 'plus'"></b-icon> Nueva
              </b-button>
            </div>

            <div class="messages-inbox-staff-picker" v-if="showStaffPicker">
              <div style="position:relative">
                <b-form-input ref="staffSearchInput"
                              v-model="staffSearch.query"
                              size="sm"
                              placeholder="Buscar por nombre..."
                              autocomplete="off"
                              @keyup="onStaffSearchKeyup($event)"
                              @keydown.down.prevent="staffSearchMoveDown"
                              @keydown.up.prevent="staffSearchMoveUp"
                              @keydown.enter.prevent="staffSearchConfirm"
                              @blur="staffSearchBlur"
                              @focus="staffSearchFocus">
                </b-form-input>

                <div class="messages-staff-search-results" v-if="staffSearch.show" ref="staffSearchDropdown">
                  <div v-if="staffSearch.loading" class="messages-staff-search-empty text-muted">
                    <b-spinner small class="mr-1"></b-spinner> Buscando...
                  </div>
                  <template v-else>
                    <div v-for="(item, idx) in staffSearch.results"
                         :key="item.id"
                         class="messages-staff-search-item"
                         :class="{ 'messages-staff-search-item--active': idx === staffSearch.activeIdx }"
                         @mousedown.prevent="selectStaffResult(item)">
                      <b-avatar size="1.75rem" :src="item.image" :text="avatarInitial(item.name)" variant="light" class="mr-2"></b-avatar>
                      <span>{{ item.name }}</span>
                    </div>

                    <div v-if="!staffSearch.results.length" class="messages-staff-search-empty text-muted">
                      Sin resultados para "{{ staffSearch.query }}"
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div class="messages-inbox-conversations">
              <div v-for="item in conversations"
                   :key="item.id"
                   class="messages-inbox-conversation-item"
                   :class="{ 'messages-inbox-conversation-item--active': activeConversation && activeConversation.id === item.id }"
                   @click="openConversation(item)">
                <b-avatar size="2.25rem" :src="item.staff_image" :text="avatarInitial(item.staff_name)" variant="light" class="mr-2"></b-avatar>
                <div class="messages-inbox-conversation-body">
                  <div class="d-flex justify-content-between align-items-baseline">
                    <span class="messages-inbox-conversation-name">{{ item.staff_name }}</span>
                    <span class="messages-inbox-conversation-time" v-if="item.last_message_at">{{ formatDate(item.last_message_at) }}</span>
                  </div>
                  <div class="messages-inbox-conversation-preview">{{ item.last_message || 'Sin mensajes todavía' }}</div>
                </div>
                <b-badge v-if="item.unread_count" variant="danger" pill class="ml-1">{{ item.unread_count > 99 ? '99+' : item.unread_count }}</b-badge>
                <b-icon icon="envelope-fill"
                        class="messages-inbox-mark-unread"
                        v-b-tooltip.hover
                        title="Marcar como no leída"
                        @click.stop="markUnread(item)"></b-icon>
              </div>

              <b-alert v-if="!loadingConversations && !conversations.length" variant="warning" show class="m-2">
                Todavía no tenés conversaciones. Usá "Nueva" para escribirle a alguien del staff.
              </b-alert>
            </div>
          </b-col>

          <b-col md="8" class="messages-inbox-thread-col">
            <template v-if="activeConversation">
              <div class="messages-inbox-thread-header">
                <b-avatar size="1.75rem" :src="activeConversation.staff_image" :text="avatarInitial(activeConversation.staff_name)" variant="light" class="mr-2"></b-avatar>
                <strong>{{ activeConversation.staff_name }}</strong>
              </div>

              <div class="messages-inbox-thread-body" ref="threadBody">
                <div v-if="loadingMessages" class="text-center text-muted py-4">
                  <b-spinner small class="mr-2"></b-spinner> Cargando...
                </div>
                <template v-else>
                  <div v-for="message in messages"
                       :key="message.id"
                       class="message-bubble-row"
                       :class="isOwn(message) ? 'message-bubble-row--own' : 'message-bubble-row--other'">
                    <div class="message-bubble" :class="isOwn(message) ? 'message-bubble--own' : 'message-bubble--other'">
                      <div class="message-bubble-body">{{ message.body }}</div>
                      <div class="message-bubble-time">{{ formatTime(message.created_at) }}</div>
                    </div>
                  </div>

                  <div class="text-center text-muted py-4" v-if="!messages.length">
                    Escribí el primer mensaje
                  </div>
                </template>
              </div>

              <div class="messages-inbox-thread-composer">
                <app-textarea v-model="newMessageBody"
                                  rows="1"
                                  max-rows="4"
                                  placeholder="Escribir un mensaje..."
                                  :disabled="sending"
                                  @keydown.enter.exact.prevent="sendMessage">
                </app-textarea>
                <b-button variant="primary" :disabled="sending || !newMessageBody || !newMessageBody.trim()" @click="sendMessage()">
                  <b-spinner small v-if="sending"></b-spinner>
                  <b-icon icon="arrow-up-circle-fill" v-else></b-icon>
                </b-button>
              </div>
            </template>
            <template v-else>
              <div class="messages-inbox-thread-empty">
                <b-icon icon="chat-square-text" font-scale="2.5" class="mb-2"></b-icon>
                <div>Seleccioná una conversación o iniciá una nueva</div>
              </div>
            </template>
          </b-col>
        </b-row>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row>
          <b-col>
            <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
              <i class="fa fa-angle-double-left"></i>
              Volver
            </b-button>
          </b-col>
        </b-row>
      </b-card>

    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Session from '@/handler/session'
  import moment from 'moment'

  const POLL_INTERVAL_MS = 30000
  const THREAD_POLL_INTERVAL_MS = 7000
  const STAFF_SEARCH_DEBOUNCE_MS = 250

  export default {
    data: () => {
      return {
        access: {
          module_id: Modules.MENSAJES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'inbox',
          elements: {}
        },
        loadingConversations: false,
        loadingMessages: false,
        conversations: [],
        activeConversation: null,
        messages: [],
        newMessageBody: '',
        sending: false,
        showStaffPicker: false,
        staffSearch: {
          query: '',
          results: [],
          loading: false,
          show: false,
          activeIdx: -1,
          focusTarget: 'input',
          _timer: null,
        },
        pollTimer: null,
        threadPollTimer: null,
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    mounted() {
      this.loadingConversations = true
      this.fetchConversations().then(() => {
        if (this.$route.params.conversationId) {
          var match = this.conversations.find((item) => item.id == this.$route.params.conversationId)
          if (match) this.openConversation(match)
        }
      })
      this.startPolling()
    },
    beforeDestroy() {
      this.stopPolling()
      this.stopThreadPolling()
    },
    methods: {
      startPolling() {
        this.pollTimer = setInterval(() => {
          this.fetchConversations()
        }, POLL_INTERVAL_MS)
      },
      stopPolling() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
        }
      },
      startThreadPolling() {
        this.stopThreadPolling()
        this.threadPollTimer = setInterval(() => {
          this.fetchMessages()
        }, THREAD_POLL_INTERVAL_MS)
      },
      stopThreadPolling() {
        if (this.threadPollTimer) {
          clearInterval(this.threadPollTimer)
          this.threadPollTimer = null
        }
      },
      fetchConversations() {
        return serviceAPI.getConversations()
          .then((response) => {
            this.conversations = response.data
            this.loadingConversations = false
          })
          .catch((error) => {
            this.loadingConversations = false
            this.$awn.alert(Error.showError(error))
          })
      },
      markUnread(item) {
        serviceAPI.markUnread(item.id)
          .then(() => {
            this.fetchConversations()
          })
          .catch((error) => {
            this.$awn.alert(Error.showError(error))
          })
      },
      toggleStaffPicker() {
        this.showStaffPicker = !this.showStaffPicker

        if (this.showStaffPicker) {
          this.$nextTick(() => {
            if (this.$refs.staffSearchInput) this.$refs.staffSearchInput.focus()
          })
        } else {
          this.resetStaffSearch()
        }
      },
      resetStaffSearch() {
        clearTimeout(this.staffSearch._timer)
        clearTimeout(this._staffBlurTimer)
        this.staffSearch.query = ''
        this.staffSearch.results = []
        this.staffSearch.show = false
        this.staffSearch.activeIdx = -1
        this.staffSearch.focusTarget = 'input'
      },
      onStaffSearchKeyup(event) {
        // Teclas de navegación/control: no deben disparar una búsqueda nueva
        // (si no, cada flecha resetea la selección y "refresca" el dropdown).
        var ignoredKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock']
        if (event && ignoredKeys.indexOf(event.key) !== -1) return

        clearTimeout(this.staffSearch._timer)

        var q = this.staffSearch.query.trim()
        if (!q) {
          this.staffSearch.results = []
          this.staffSearch.show = false
          return
        }

        this.staffSearch._timer = setTimeout(() => this.doStaffSearch(), STAFF_SEARCH_DEBOUNCE_MS)
      },
      doStaffSearch() {
        var q = this.staffSearch.query.trim()
        if (!q) return

        this.staffSearch.loading = true
        this.staffSearch.show = true
        this.staffSearch.activeIdx = -1

        serviceAPI.filterStaffByName({ name: q })
          .then((response) => {
            this.staffSearch.loading = false
            this.staffSearch.results = response.data || []
          })
          .catch(() => {
            this.staffSearch.loading = false
            this.staffSearch.results = []
          })
      },
      staffSearchMoveDown() {
        this.staffSearch.focusTarget = 'dropdown'
        if (this.staffSearch.activeIdx < this.staffSearch.results.length - 1) {
          this.staffSearch.activeIdx++
          this.$nextTick(() => this.scrollActiveStaffResult())
        }
      },
      staffSearchMoveUp() {
        this.staffSearch.focusTarget = 'dropdown'
        if (this.staffSearch.activeIdx > -1) {
          this.staffSearch.activeIdx--
          this.$nextTick(() => this.scrollActiveStaffResult())
        }
      },
      scrollActiveStaffResult() {
        var container = this.$refs.staffSearchDropdown
        if (!container) return
        var active = container.querySelector('.messages-staff-search-item--active')
        if (active) active.scrollIntoView({ block: 'nearest' })
      },
      staffSearchConfirm() {
        if (!this.staffSearch.show || this.staffSearch.loading) {
          this.doStaffSearch()
          return
        }

        var idx = this.staffSearch.activeIdx
        if (idx >= 0 && idx < this.staffSearch.results.length) {
          this.selectStaffResult(this.staffSearch.results[idx])
        }
      },
      staffSearchBlur() {
        this._staffBlurTimer = setTimeout(() => { this.staffSearch.show = false }, 200)
      },
      staffSearchFocus() {
        this.staffSearch.focusTarget = 'input'
        if (this.staffSearch.results.length > 0) {
          this.staffSearch.show = true
        }
      },
      selectStaffResult(staff) {
        clearTimeout(this._staffBlurTimer)
        this.startConversation(staff)
      },
      startConversation(staffObject) {
        if (!staffObject) return

        serviceAPI.startConversation(staffObject.id)
          .then((response) => {
            this.showStaffPicker = false
            this.resetStaffSearch()
            this.fetchConversations()
            this.openConversation({
              id: response.data.id,
              staff_id: response.data.staff_id,
              staff_name: response.data.staff_name,
              staff_image: response.data.staff_image,
              unread_count: 0,
            })
          })
          .catch((error) => {
            this.$awn.alert(Error.showError(error))
          })
      },
      openConversation(conversation) {
        this.activeConversation = conversation
        this.showStaffPicker = false
        this.resetStaffSearch()
        this.messages = []
        this.loadingMessages = true
        this.fetchMessages()
        this.startThreadPolling()
      },
      fetchMessages() {
        if (!this.activeConversation) return

        serviceAPI.getMessages(this.activeConversation.id)
          .then((response) => {
            this.messages = response.data
            this.loadingMessages = false
            this.$nextTick(() => this.scrollToBottom())
          })
          .catch((error) => {
            this.loadingMessages = false
            this.$awn.alert(Error.showError(error))
          })
      },
      sendMessage() {
        if (!this.newMessageBody || !this.newMessageBody.trim()) return
        if (!this.activeConversation) return

        var body = this.newMessageBody
        this.sending = true

        serviceAPI.sendMessage(this.activeConversation.id, body)
          .then((response) => {
            this.newMessageBody = ''
            this.sending = false
            this.messages.push(response.data)
            this.$nextTick(() => this.scrollToBottom())
          })
          .catch((error) => {
            this.sending = false
            this.$awn.alert(Error.showError(error))
          })
      },
      scrollToBottom() {
        var el = this.$refs.threadBody
        if (el) el.scrollTop = el.scrollHeight
      },
      isOwn(message) {
        var currentStaffId = Session.getSession().auth.user.selector_session
        return message.staff_id == currentStaffId
      },
      avatarInitial(name) {
        return name ? name.charAt(0).toUpperCase() : '?'
      },
      formatDate(date) {
        return moment(date).fromNow()
      },
      formatTime(date) {
        return moment(date).format('HH:mm')
      },
    }
  }
</script>
<style scoped>
  .messages-inbox-list-col {
    border-right: 1px solid #eee;
    padding: 0;
  }
  .messages-inbox-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
  }
  .messages-inbox-list-title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #3a4248;
  }
  .messages-inbox-staff-picker {
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
    background: #f8fafc;
  }
  .messages-staff-search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #dee2e6;
    border-top: none;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 4px 14px rgba(0,0,0,0.13);
    z-index: 200;
    max-height: 260px;
    overflow-y: auto;
  }
  .messages-staff-search-empty {
    padding: 0.6rem 0.85rem;
    font-size: 0.82rem;
  }
  .messages-staff-search-item {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.85rem;
    cursor: pointer;
    border-bottom: 1px solid #f0f2f5;
    font-size: 0.85rem;
    transition: background-color 0.1s;
  }
  .messages-staff-search-item:last-child {
    border-bottom: none;
  }
  .messages-staff-search-item:hover,
  .messages-staff-search-item--active {
    background: #f0f4ff;
  }
  .messages-inbox-conversations {
    max-height: 560px;
    overflow-y: auto;
  }
  .messages-inbox-conversation-item {
    display: flex;
    align-items: flex-start;
    padding: 10px 14px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
  }
  .messages-inbox-conversation-item:hover {
    background-color: #f8fafc;
  }
  .messages-inbox-conversation-item--active {
    background-color: #eef6ff;
  }
  .messages-inbox-mark-unread {
    flex: 0 0 auto;
    align-self: center;
    margin-left: 6px;
    font-size: 13px;
    color: #adb5bd;
    opacity: 0;
    transition: opacity 0.15s ease, color 0.15s ease;
  }
  .messages-inbox-conversation-item:hover .messages-inbox-mark-unread {
    opacity: 1;
  }
  .messages-inbox-mark-unread:hover {
    color: #007bff;
  }
  .messages-inbox-conversation-body {
    flex: 1;
    min-width: 0;
  }
  .messages-inbox-conversation-name {
    font-size: 13px;
    font-weight: 600;
    color: #3a4248;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .messages-inbox-conversation-time {
    flex: 0 0 auto;
    font-size: 10px;
    color: #adb5bd;
  }
  .messages-inbox-conversation-preview {
    font-size: 12px;
    color: #6c757d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .messages-inbox-thread-col {
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .messages-inbox-thread-header {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
  }
  .messages-inbox-thread-body {
    flex: 1;
    min-height: 420px;
    max-height: 560px;
    overflow-y: auto;
    padding: 14px;
    background: #f8fafc;
  }
  .messages-inbox-thread-composer {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid #eee;
  }
  .messages-inbox-thread-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 480px;
    color: #adb5bd;
  }
</style>
<style>
  .message-bubble-row {
    display: flex;
    margin-bottom: 8px;
  }
  .message-bubble-row--own {
    justify-content: flex-end;
  }
  .message-bubble-row--other {
    justify-content: flex-start;
  }
  .message-bubble {
    max-width: 60%;
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.35;
  }
  .message-bubble--own {
    background: #007bff;
    color: #fff;
    border-bottom-right-radius: 3px;
  }
  .message-bubble--other {
    background: #e9ecef;
    color: #3a4248;
    border-bottom-left-radius: 3px;
  }
  .message-bubble-body {
    word-break: break-word;
    white-space: pre-wrap;
  }
  .message-bubble-time {
    margin-top: 3px;
    font-size: 9px;
    opacity: 0.75;
    text-align: right;
  }
</style>
